import {
  getAnnotationFrameMeta,
  type AnnotationFrameMetaResp,
  type AnnotationRequestOptions,
} from "@/api";

type FetchFrameMeta = (
  datasetUuid: string,
  camera: string,
  episode: string,
  options?: AnnotationRequestOptions,
) => Promise<AnnotationFrameMetaResp>;

/**
 * episode 영상의 frame 수와 fps 는 세션 동안 바뀌지 않는다. capture 와 tracking 이 각자 받으면
 * 동작마다 요청이 하나씩 늘어난다. 그래서 같은 (dataset, camera, episode)는 한 번만 받아 공유한다.
 *
 * 진행 중 요청에는 호출자의 signal 을 전달하지 않는다. 공유 요청이라 한 호출자의 취소가 다른
 * 호출자의 동작까지 끊기 때문이다. 취소한 호출자는 결과를 받지 않고 자기 signal 의 사유로 거절된다.
 */
export class AnnotationFrameMetaCache {
  private readonly cache = new Map<string, AnnotationFrameMetaResp>();
  private readonly inflight = new Map<string, Promise<AnnotationFrameMetaResp>>();

  constructor(private readonly fetchFrameMeta: FetchFrameMeta = getAnnotationFrameMeta) {}

  get: FetchFrameMeta = async (datasetUuid, camera, episode, options) => {
    const key = JSON.stringify([datasetUuid, camera, episode]);
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;
    const pending = this.inflight.get(key) ?? this.fetch(key, datasetUuid, camera, episode);
    this.inflight.set(key, pending);
    try {
      const meta = await pending;
      if (options?.signal?.aborted === true) throw options.signal.reason;
      return meta;
    } finally {
      if (this.inflight.get(key) === pending) this.inflight.delete(key);
    }
  };

  clear(): void {
    this.cache.clear();
    this.inflight.clear();
  }

  private async fetch(
    key: string,
    datasetUuid: string,
    camera: string,
    episode: string,
  ): Promise<AnnotationFrameMetaResp> {
    const meta = await this.fetchFrameMeta(datasetUuid, camera, episode);
    this.cache.set(key, meta);
    return meta;
  }
}
